import { useEffect, useState } from "react";
import {
  Button,
  Select,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { buildServiceUrl } from "../../../utils/urlBuilder";
import "./ActivateUserForm.css";

const EDITABLE_ROLE_VALUES = ["data_provider", "data_consumer", "data_owner"];

export default function EditUserRolesForm({
  userInfo,
  closePopover,
  onUpdateTable,
  setTableData,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = user?.access_token || user?.id_token || "";

  const [rolesSelected, setRolesSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const roles = [
    { value: "data_provider", label: t("roles.data_provider") },
    { value: "data_consumer", label: t("roles.data_consumer") },
    { value: "data_owner", label: t("roles.data_owner") },
  ];

  // Cargar roles actuales del usuario al abrir
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const res = await axios.get(
          `${buildServiceUrl(
            "keycloak",
            `admin/realms/DSB/users/${userInfo.id}/role-mappings/realm`
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const currentRoles = res.data
          .map((r) => r.name)
          .filter((r) => EDITABLE_ROLE_VALUES.includes(r));
        setRolesSelected(currentRoles);
      } catch (err) {
        console.error("Error fetching user roles", err);
      }
    };
    if (userInfo?.id) fetchUserRoles();
  }, [userInfo, token]);

  const handleConfirm = async () => {
    if (!rolesSelected.length) return;
    setLoading(true);

    try {
      // 1. Obtener roles actuales
      const currentRes = await axios.get(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userInfo.id}/role-mappings/realm`
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const currentRoles = currentRes.data;

      // 2. Eliminar roles actuales
      if (currentRoles.length > 0) {
        await axios.delete(
          `${buildServiceUrl(
            "keycloak",
            `admin/realms/DSB/users/${userInfo.id}/role-mappings/realm`
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: currentRoles,
          }
        );
      }

      // 3. Obtener objetos de roles seleccionados
      const roleObjs = await Promise.all(
        rolesSelected.map(async (r) => {
          const res = await axios.get(
            `${buildServiceUrl("keycloak", `admin/realms/DSB/roles/${r}`)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return res.data;
        })
      );

      // 4. Asignar "default-roles-dsb"
      const defaultRoleRes = await axios.get(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/roles/default-roles-dsb`
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      roleObjs.push(defaultRoleRes.data);

      // 5. Assignar nuevos roles
      await axios.post(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userInfo.id}/role-mappings/realm`
        )}`,
        roleObjs,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(t("toaster.editRole"));
      closePopover();
      if (onUpdateTable && setTableData) {
        const updated = await onUpdateTable();
        setTableData(updated);
      }
    } catch (err) {
      toast.error(t("toaster.userError"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => closePopover();

  if (!userInfo) return null;

  return (
    <div className="activate-user-form">
      <div className="activate-header">
        <Typography variant="h6" className="activate-title">
          {userInfo.firstName} {userInfo.lastName}
        </Typography>
        <IconButton aria-label="close" onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <div className="activate-body">
        <Select
          multiple
          value={rolesSelected}
          onChange={(e) => setRolesSelected(e.target.value)}
          displayEmpty
          fullWidth
          className="activate-select"
          renderValue={(selected) =>
            selected.length
              ? selected
                  .map((s) => roles.find((r) => r.value === s)?.label)
                  .join(", ")
              : t("profilePage.selectRole")
          }
        >
          {roles.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="activate-footer">
        <Button variant="outlined" onClick={handleCancel} disabled={loading}>
          {t("profilePage.cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "var(--main-color)",
          }}
          onClick={handleConfirm}
          disabled={!rolesSelected.length || loading}
        >
          {loading ? "Procesando..." : t("profilePage.save")}
        </Button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
